import React, { useState, useEffect, useRef } from "react";
import * as Material from "@mui/material";
import { Button } from "@fragments";
import {
    RemoveCircleRounded,
    ReportProblemRounded,
    DoneAllRounded,
    AutorenewRounded,
    AddRounded,
    RefreshRounded
} from "@mui/icons-material";

import api from "@helpers/api.js";
import { ReactComponent as GinasteeCard } from '@assets/draws/card-ginastee.svg';
import CreateBankAccountModal from "@components/_modals/Billing/CreateBankAccountModal";

import "./styles.scss";

export default function BankOnboardingCard() {
    const REF_createBankAccountModal = useRef();

    function handleOpenNewAccountModal(callback) {
        REF_createBankAccountModal.current.open(() => {
            if (callback) callback();
        });
    }

    const [loading, setLoading] = useState(true);
    const [account, setAccount] = useState(null);


    useEffect(() => {
        if (loading) handleLoad();
    }, [loading]);

    async function handleLoad() {
        try {
            const { data } = await api.get("wallet/status");

            setAccount({ ...data });

        } catch (e) {
            console.log(e);
        }

        setLoading(false);
    }

    function StatusBadge({ status }) {
        const statusText = {
            'pending': "Aguardando envio",
            'rejected': "Reprovado",
            'completed': "Concluído",
            'approved': "Aprovado",
            'awaiting_approval': "Em análise",
        }[status] || 'Não iniciado'

        const statusColor = {
            'pending': 'default',
            'rejected': 'error',
            'approved': 'success',
            'completed': 'success',
            'awaiting_approval': 'info',
        }[status] || 'default'

        const statusIcon = {
            'pending': <RemoveCircleRounded />,
            'rejected': <ReportProblemRounded />,
            'completed': <DoneAllRounded />,
            'approved': <DoneAllRounded />,
            'awaiting_approval': <AutorenewRounded className="analysis-animation" />,
        }[status] || <RemoveCircleRounded />

        return (
            <div className={`acct-status-badge ${statusColor}`}>
                {statusIcon}
                <small style={{ fontSize: '12.99px', transform: 'translateY(1px)' }}>{statusText}</small>
            </div>
        )
    }

    function Step({
        header = null,
        left = null,
        children = null
    }) {

        return (
            <div className="account-creation-step py-4">
                <div className="account-creation-step-left">{left}</div>
                <div className="account-creation-step-children pr-1">
                    {header}
                    {children}
                </div>
            </div>
        )
    }

    function StepHeader({ title, titleSize = "small", description, right, left }) {
        const TitleComponent = {
            'small': <h5 className="semiBold">{title}</h5>,
            'medium': <h3 className="bold">{title}</h3>,
            'large': <h2 className="bold">{title}</h2>,
        }[titleSize] || <h4 className="bold">{title}</h4>

        return (
            <div className="account-creation-step-header-container">
                {left}
                <div className="account-creation-step-header">
                    {TitleComponent}
                    {
                        description && <h5 style={{ marginTop: '-5px' }} className="create-account-subtitle">{description}</h5>
                    }
                </div>
                {right}
            </div>
        )
    }

    const StepsList = {
        AccountCreation() {
            const [timeoutForRefresh, setTimeoutForRefresh] = useState(0);

            useEffect(() => {
                console.log('timeoutForRefresh', timeoutForRefresh);
                if (timeoutForRefresh > 0) {
                    setTimeout(() => {
                        if (timeoutForRefresh === 1) {
                            handleLoad();
                        }
                        setTimeoutForRefresh(timeoutForRefresh - 1)
                    }, 1000)
                }
            }, [timeoutForRefresh]);

            function handleTrigger() {
                handleOpenNewAccountModal(() => {
                    console.log('callback');
                    setTimeoutForRefresh(18)
                });
            }

            const created = (!!account?.wallet?.id || timeoutForRefresh > 0);

            return (
                <Step
                    key={timeoutForRefresh}
                    left={<>
                        {
                            loading
                                ? <Material.Skeleton variant="text" width={100} height={20} />
                                : <StatusBadge status={created ? "completed" : "pending"} />
                        }
                    </>}
                    header={
                        <>
                            <StepHeader
                                title="Passo #01: Cadastrar conta bancária"
                                titleSize="medium"
                                description={
                                    created ? "Conta cadastrada com sucesso" : "Informe qual será a conta onde serão depositados os valores das vendas realizadas na sua plataforma."
                                }
                                right={!created && !loading &&
                                    <Button
                                        onClick={handleTrigger}
                                        endIcon={<AddRounded />}
                                        text="Cadastrar conta"
                                    />
                                }
                            />

                            {timeoutForRefresh > 0 && (
                                <>
                                    <h5 className="create-account-subtitle account-progress-title mt-2">Sua conta está sendo criada...</h5>
                                    <div className="account-creation-progress">
                                        <Material.LinearProgress
                                            variant="determinate"
                                            value={((18 - timeoutForRefresh) / 18) * 100}
                                            className="account-creation-progress-bar"
                                        />
                                        <h5 className="account-creation-progress-text">
                                            {timeoutForRefresh} segundo{timeoutForRefresh !== 1 ? 's' : ''} restante{timeoutForRefresh !== 1 ? 's' : ''}
                                        </h5>
                                    </div>
                                </>
                            )}
                        </>

                    }
                />
            )
        },

        DocumentsAttachment() {

            const documents = account?.documents || [];


            const docsConfig = {
                'entrepreneur_requirement': "Requerimento de empresário (para conta ME)",
                'identification_selfie': "Selfie",
                'identification': "Foto do RG ou CPF",
                'minutes_of_election': "Ata de eleição (apenas para associações)",
                'social_contract': "Contrato social (para empresas LTDA)",
            }

            async function handleDocumentClick(docItem, setLoading) {
                if (docItem.url) {
                    window.open(docItem.url, '_blank');
                    return;
                }

                function fileToBase64(file) {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });
                }

                const input = window.document.createElement('input');
                input.type = 'file';
                input.accept = 'image/png,image/jpeg,image/jpg';
                input.onchange = async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;

                    const maxBytes = 20 * 1024 * 1024; // 20MB
                    if (file.size > maxBytes) {
                        window.setSnackbar && window.setSnackbar('O arquivo deve ter no máximo 20MB.');
                        return;
                    }

                    const allowedMime = ['image/png', 'image/jpeg', 'application/pdf'];
                    const allowedExt = ['png', 'jpg', 'jpeg', 'pdf'];
                    const fileExt = (file.name.split('.').pop() || '').toLowerCase();
                    const isValidType = allowedMime.includes(file.type) || allowedExt.includes(fileExt);
                    if (!isValidType) {
                        window.setSnackbar && window.setSnackbar('Apenas arquivos PNG, JPG ou PDF são permitidos.');
                        return;
                    }

                    try {
                        setLoading && setLoading(true);
                        const dataUrl = await fileToBase64(file);

                        await api.post('wallet/document', {
                            id: docItem.id,
                            type: docItem.type,
                            document: dataUrl,
                        });

                        window.setSnackbar && window.setSnackbar('Documento enviado com sucesso.');
                        await handleLoad();
                    } catch (e) {
                        const message = (e && e.response && e.response.data && e.response.data.message) || e.message || 'Falha ao enviar documento.';
                        window.setSnackbar && window.setSnackbar(message);
                    } finally {
                        setLoading && setLoading(false);
                        input.remove();
                    }
                };

                input.click();
            }

            function DocumentRow({ document }) {
                const [displayRefreshButton, setDisplayRefreshButton] = useState(false);


                async function handleRefreshList() {
                    setLoading(true);
                    await handleLoad();
                    setDisplayRefreshButton(false);
                }

                return (
                    <StepHeader
                        title={docsConfig[document.type]}
                        left={<StatusBadge status={document.status} />}
                        right={
                            <>
                                {
                                    ['pending', 'rejected'].includes(document.status) && <Button
                                        onClick={async () => {
                                            setDisplayRefreshButton(true);
                                            await handleDocumentClick(document, setLoading);
                                        }}
                                        endIcon={<AddRounded />}
                                        text="Enviar documento"
                                    />
                                }

                                {
                                    displayRefreshButton && <Button
                                        onClick={handleRefreshList}
                                        endIcon={<RefreshRounded />}
                                        text="Atualizar"
                                    />
                                }

                            </>
                        }
                    />
                )
            }

            return (
                <Step
                    left={<>
                        {
                            loading
                                ? <Material.Skeleton variant="text" width={100} height={20} />
                                : <StatusBadge status={account?.wallet?.statuses?.documentation === 'awaiting_approval' ? 'awaiting_approval' : 'pending'} />
                        }
                    </>}
                    header={
                        <StepHeader
                            title="Passo #02: Validação de identidade"
                            titleSize="medium"
                            description="Para que sua conta seja aprovada, envie os documentos abaixo"
                        />
                    }
                >
                    <div className="mt-1" />
                    {
                        !loading && documents.map((document, index) => (
                            <DocumentRow key={index} document={document} />
                        ))
                    }
                </Step>
            )
        },

        Analysis() {
            const isInAnalysis = account?.wallet?.statuses?.commercial === 'awaiting_approval' || account?.statuses?.documentation === 'awaiting_approval' || account?.statuses?.general === 'awaiting_approval';
            return (
                <Step
                    left={<>
                        {
                            loading
                                ? <Material.Skeleton variant="text" width={100} height={20} />
                                : <StatusBadge status={isInAnalysis ? 'awaiting_approval' : 'pending'} />
                        }
                    </>}
                    header={
                        <StepHeader
                            title="Passo #03: Análise"
                            titleSize="medium"
                            description="As informações serão enviadas para análise, que será retornada dentro de 48h após o envio dos documentos."
                        />
                    }
                >
                    <div className="mt-1" />

                    <StepHeader
                        title="Validação da conta cadastrada"
                        left={<StatusBadge status={account?.wallet?.statuses?.commercial} />}
                    />

                    <StepHeader
                        title="Validação dos documentos"
                        left={<StatusBadge status={account?.wallet?.statuses?.documentation} />}
                    />

                    <StepHeader
                        title="Confirmação geral do cadastro"
                        left={<StatusBadge status={account?.wallet?.statuses?.general} />}
                    />
                </Step>
            )
        }
    }

    return (
        <>
            <div className="account-creation-interface pt-3">
                <div className="account-creation-container-header">
                    <GinasteeCard className="account-creation-card-effect-header" />
                    <div>
                        <h2 style={{ marginTop: '-2px' }}>
                            Ginastee Wallet
                        </h2>
                        <p className="create-account-subtitle">
                            Cadastre sua conta de recebíveis para receber pagamentos no Ginastee                </p>
                    </div>
                </div>

                <div className="account-creation-box mt-6 mb-6">
                    <StepsList.AccountCreation />

                    <Material.Divider className="acc-creation-divider" flexItem />

                    <StepsList.DocumentsAttachment />

                    <Material.Divider className="acc-creation-divider" flexItem />

                    <StepsList.Analysis />
                </div>
            </div>

            <CreateBankAccountModal modalRef={REF_createBankAccountModal} />
        </>
    )
}
